import { useState } from "react";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { DEFAULT_GL_ACCOUNT, GlAccount } from "../../../../../hooks/useGLAccounts.ts";
import GLAPill from "../../../../../components/specific/GLAPill.tsx";

function filterByQuery(accounts: GlAccount[], query: string): GlAccount[] {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => a.label_nl.toLowerCase().includes(q));
}

export function GLAListBox({
    glAccounts,
    value,
    onChangeFunc,
}: {
    glAccounts: GlAccount[];
    value: string | null;
    onChangeFunc: (value: GlAccount) => void;
}) {
    const [query, setQuery] = useState("");
    const options: GlAccount[] = [...glAccounts].sort((a, b) => a.ranking - b.ranking);
    const filtered = filterByQuery(options, query);
    const displayValue: GlAccount =
        value === null
            ? DEFAULT_GL_ACCOUNT
            : options.find((g) => g.id === value) ?? DEFAULT_GL_ACCOUNT;

    return (
        <Combobox
            as="div"
            className="w-96 ml-auto"
            value={displayValue}
            by="id"
            onChange={(selected: GlAccount) => onChangeFunc(selected)}
        >
            <div className="relative mt-2">
                <ComboboxInput
                    className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                    onChange={(e) => setQuery(e.target.value)}
                    onBlur={() => setQuery("")}
                    displayValue={(v: GlAccount) => v?.label_nl ?? ""}
                />
                <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 pr-0 focus:outline-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </ComboboxButton>
                <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {filtered.map((account) => (
                        <ComboboxOption
                            key={account.id}
                            value={account}
                            className="group relative cursor-default select-none py-2 pr-4 text-gray-900 data-[focus]:bg-blue-500 data-[focus]:text-white flex items-center mx-1 gap-2 rounded-md px-3"
                        >
                            <GLAPill icon={account.icon} label={account.label_nl} />
                        </ComboboxOption>
                    ))}
                </ComboboxOptions>
            </div>
        </Combobox>
    );
}
